import { Helmet } from 'react-helmet-async'

const SITE_URL = 'https://getintervio.com'
const SITE_NAME = 'Intervio'
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`

interface SEOProps {
  /** Page title — will be appended with " | Intervio" unless `rawTitle` is true */
  title?: string
  /** Meta description */
  description?: string
  /** Canonical path, e.g. "/features" (relative to SITE_URL) */
  path?: string
  /** Override the OG image URL */
  ogImage?: string
  /** OG type — defaults to "website" */
  ogType?: string
  /** Don't append the site name to the title */
  rawTitle?: boolean
  /** Additional JSON-LD structured data (array of objects) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /** Prevent indexing for this page */
  noIndex?: boolean
}

function SEO({
  title,
  description = 'AI-powered interview tool with real-time answer hints, transcription, and screen OCR. Choose cloud or local processing. Ace your next technical or behavioral interview.',
  path = '',
  ogImage = DEFAULT_OG_IMAGE,
  ogType = 'website',
  rawTitle = false,
  jsonLd,
  noIndex = false,
}: SEOProps) {
  const fullTitle = rawTitle || !title
    ? (title || `${SITE_NAME} — AI-Powered Real-Time Interview Hints`)
    : `${title} | ${SITE_NAME}`

  const canonicalUrl = `${SITE_URL}${path}`

  // Build JSON-LD array
  const jsonLdScripts: Record<string, unknown>[] = []

  // Always include Organization + WebSite on every page
  jsonLdScripts.push({
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/favicon.svg`,
    description: 'AI-powered interview assistant providing real-time answer hints, transcription, and screen OCR.',
    sameAs: [],
  })

  jsonLdScripts.push({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: SITE_URL,
    description,
  })

  // Page-specific JSON-LD
  if (jsonLd) {
    if (Array.isArray(jsonLd)) {
      jsonLdScripts.push(...jsonLd)
    } else {
      jsonLdScripts.push(jsonLd)
    }
  }

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonicalUrl} />

      {noIndex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD Structured Data */}
      {jsonLdScripts.map((data, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(data)}
        </script>
      ))}
    </Helmet>
  )
}

export { SITE_URL, SITE_NAME }
export default SEO
